import { ReactNode, useState } from 'react';

import { doRequest } from '@r1-frontend/do-request/index';
import { backCall } from '@r1-frontend/api-domru/core-requests/api/v1/call/back';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import PopupResult, { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { ISubmitParams, TField } from '@r1-frontend/ui-react/organisms/Forms/NamePhoneForm';
import { ACTIONS } from '@r1-frontend/shared/constants/analytics';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import { POLICY_PAGE_LINK } from '~/src/constants';
import { dataLayerForCBPhonePush } from '~/src/helpers/eventsForDataLayer';

import * as ST from './styled';

const CALLBACK_LABEL = 'Получить консультацию';

interface IResult {
    ok: boolean,
    message: string,
}

const defaultResult: IResult = {
    ok: false,
    message: '',
};

interface IProps {
    children: ReactNode,
}

const CallbackForm = ({ children }: IProps) => {
    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);

    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<IResult>(defaultResult);
    const [isStartInput, setIsStartInput] = useState(false);

    const handleResult = (res: IResult) => {
        setResult(() => res);
        dataLayerForCBPhonePush({
            action: result.ok ? ACTIONS.startedService : ACTIONS.errorService,
            option: CALLBACK_LABEL,
        });
    };

    const cleanUp = (name: TField, phone: TField) => {
        name.clearInput();
        phone.clearInput();
        name.isValid = false;
        phone.isValid = false;
        setIsStartInput(false);
        setIsLoading(false);
    };

    const handleSubmit = async(params?: ISubmitParams) => {
        if (!params) {
            return;
        }

        setIsLoading(true);
        const { name, phone } = params;
        const resp = await doRequest.coreRequests<unknown>(
            backCall({
                phoneNumber: phone.value,
            }),
        );

        if (resp.isSuccess) {
            handleResult({
                ok: true,
                message: 'Заявка успешно создана.',
            });
        } else {
            handleResult({
                ok: false,
                message: 'Не удалось отправить заявку. Пожалуйста, попробуйте еще раз.',
            });
        }

        cleanUp(name, phone);
    };

    const handleChange = (inputState: TField) => {
        if (!isStartInput && inputState.value !== undefined) {
            dataLayerForCBPhonePush({
                action: ACTIONS.startedService,
                option: CALLBACK_LABEL,
            });
            setIsStartInput(true);
        }
    };

    const handleClosePopup = () => {
        setResult(() => defaultResult);
    };

    return (
        <>
            {children}
            <ST.SNamePhoneForm
                templateVariant={isMobile ? 'mobile' : 'first'}
                btnText='Позвоните мне'
                btnType={btnTypes.PRIMARY}
                policyPath={POLICY_PAGE_LINK}
                isLoading={isLoading}
                onButtonClick={handleSubmit}
                onChange={handleChange}
            />
            <PopupResult
                isOpen={!!result.message}
                onClose={handleClosePopup}
                title={result.ok ? 'Успех!' : 'Произошла ошибка'}
                text={result.message}
                type={result.ok ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR}
            />
        </>
    );
};

export default CallbackForm;
